/**
 * shouldRedressShip — the per-frame "re-dress" decision that makes a ship's visual self-heal.
 *
 * Second half of the 2026-07-15 incident fix. The retroactive re-dress in render.ts was ALL-OR-NOTHING:
 * `loadShipModel` / `loadCaptains` `await Promise.all(...)` over every class and only THEN tear the spawned
 * ships down so they rebuild with the GLB templates. A fetch that STALLS (never settles — no error, so no
 * retry either) leaves that Promise.all pending forever, and `syncShips` only rebuilt on a CLASS CHANGE —
 * so a hull born procedural (cold cache, zero templates at spawn) stayed procedural the whole session even
 * with 6/7 templates sitting loaded. Proven in prod repro: 48s+ with `tmpl:6` and the two sloops in scene
 * still `glb:false`.
 *
 * This pure function is consulted by `syncShips` for every ship every frame (behind a zero-alloc
 * short-circuit) and answers: should this existing group be torn down NOW so `buildShip` re-dresses it
 * with the assets that have arrived since it was built? Three healing cases:
 *   1. born procedural → the FIRST template arriving upgrades it (buildShip falls back to the nearest hull);
 *   2. dressed with a stand-in template (galleon/first-loaded) → its REAL class template arriving upgrades it;
 *   3. built without its helmsman/crew (captain template missing) → the captain arriving re-crews it.
 * Each rebuild flips the group's recorded state so the same case can never re-trigger — no thrash.
 * The all-or-nothing rebuilds in loadShipModel/loadCaptains remain as the fast path when nothing stalls.
 */
export function shouldRedressShip(s: {
  /** The group was built from a GLB template (render.ts sets `userData.inst` only on that branch). */
  builtWithGlb: boolean;
  /** Which class's template dressed it (`userData.tplCls`); undefined for a procedural build. */
  builtTplCls: number | undefined;
  /** The class's captain template was missing when the group was built (`userData.noCap`). */
  builtWithoutCaptain: boolean;
  /** The ship's real hull class (from the snapshot). */
  cls: number;
  /** How many ship templates are loaded right now (shipTemplates.size). */
  templatesLoaded: number;
  /** Is THIS class's ship template loaded? (shipTemplates.has(cls)) */
  clsTemplateLoaded: boolean;
  /** Is THIS class's captain template loaded? (captainTemplates.has(cls)) */
  clsCaptainLoaded: boolean;
}): boolean {
  // Born procedural: upgrade the moment ANY template exists (buildShip picks the nearest hull; if the
  // exact class lands later, case 2 finishes the job). With zero templates a rebuild would just produce
  // another procedural hull — skip.
  if (!s.builtWithGlb) return s.templatesLoaded > 0;
  // Dressed with a stand-in (galleon/first-loaded fallback): its real class template has now arrived.
  if (s.clsTemplateLoaded && s.builtTplCls !== s.cls) return true;
  // Hull is right but it sailed out captainless/crewless: the captain template has now arrived.
  if (s.builtWithoutCaptain && s.clsCaptainLoaded) return true;
  return false;
}
